// Auth flows shared by the storefront auth modal and the /login, /signup,
// /forgot-password and /verify-otp pages. Each action validates its input,
// calls the API, keeps the OTP token in sync, maps field errors onto the form
// and shows the toast. Screens only own their UI state and navigation.
//
//   login        email|phone + password  -> signs in, merges the guest cart
//   startSignup  signup form values      -> sends the signup OTP
//   startReset   email|phone             -> sends the reset-password OTP
//   verifyOtp    contact + otp           -> verifies (signup or reset)
//   resetPassword contact + new password
//   resendOtp    contact                 -> fresh OTP (+ token)
import { OTP_PURPOSE } from '@core/constants/auth';
import { CUSTOMER_TYPE, requiresVatNumber } from '@core/constants/platform';
import {
  useForgotPasswordMutation,
  useLoginMutation,
  useResendOtpMutation,
  useResetPasswordMutation,
  useSignupMutation,
  useVerifyOtpMutation,
} from '@core/hooks/useAuth';
import { useCartMerge } from '@core/hooks/useCartMerge';
import { useToast } from '@core/hooks/useToast';
import { loginSchema, signupSchema } from '@core/schemas/auth';
import { useCopy } from '@/app/providers/useCopy';
import { applyApiFieldErrors, applyZodErrors } from './applyZodErrors';
import { clearOtpToken, extractOtpToken, readOtpToken, saveOtpToken } from './otpToken';

/** How long an OTP stays valid on screen. */
export const OTP_EXPIRY_MS = 5 * 60 * 1000;

/** Signup response for an account still pending merchant approval (no OTP sent). */
export function isAwaitingApproval(resp) {
  const status = resp?.data?.approval_status;
  return Boolean(status) && String(status).toUpperCase() !== 'APPROVED';
}

/** Email or phone contact block for the auth APIs. */
export function pickContact(values, method) {
  if (method === 'phone') {
    return {
      mobile: {
        calling_code: values.mobile?.calling_code,
        number: String(values.mobile?.number || '').trim(),
      },
    };
  }
  return {
    email: String(values.email || '')
      .trim()
      .toLowerCase(),
  };
}

/**
 * Business form values -> `business_details` payload. The address uses the
 * chosen country, falling back to the store's. VAT countries send the VAT number and no GSTIN; others
 * send the GSTIN and no VAT number.
 */
export function buildBusinessDetails(details = {}, { country } = {}) {
  const addressCountry = String(details.business_address?.country || country || '')
    .trim()
    .toUpperCase();
  const { vat_number: vatNumber, gstin, business_phone: phone, ...rest } = details;
  return {
    ...rest,
    business_email: String(details.business_email || '')
      .trim()
      .toLowerCase(),
    business_address: {
      ...details.business_address,
      ...(addressCountry ? { country: addressCountry } : {}),
    },
    business_phone: {
      calling_code: phone?.calling_code,
      number: String(phone?.number || '').trim(),
    },
    ...(requiresVatNumber(addressCountry) ? { vat_number: vatNumber } : { gstin }),
  };
}

/**
 * Business signup doesn't ask for personal name / email / phone: the account's
 * name, login email and mobile come from the business details instead.
 */
export function withBusinessContact(values) {
  const details = values.business_details ?? {};
  const phone = details.business_phone;
  return {
    ...values,
    full_name: values.full_name || details.business_name,
    first_name: values.first_name || details.business_name,
    email: values.email || details.business_email,
    mobile: values.mobile?.number
      ? values.mobile
      : phone?.number
        ? { calling_code: phone.calling_code, number: phone.number }
        : undefined,
  };
}

/** Where errors for those derived fields should show on a business form. */
export const BUSINESS_CONTACT_ERROR_ALIASES = {
  full_name: ['business_details', 'business_name'],
  first_name: ['business_details', 'business_name'],
  email: ['business_details', 'business_email'],
  mobile: ['business_details', 'business_phone'],
};

/** Signup form values -> signupSchema input (full_name split, email normalised). */
export function buildSignupInput(
  rawValues,
  { includeReferral = false, isBusiness = false, country } = {},
) {
  const values = isBusiness ? withBusinessContact(rawValues) : rawValues;
  const { business_details: businessDetails, ...rest } = values;
  return {
    ...rest,
    type: isBusiness ? CUSTOMER_TYPE.BUSINESS : CUSTOMER_TYPE.INDIVIDUAL,
    ...(isBusiness ? { business_details: buildBusinessDetails(businessDetails, { country }) } : {}),
    email: String(values.email || '')
      .trim()
      .toLowerCase(),
    first_name: values.full_name?.split(' ')?.[0] ?? values.full_name,
    last_name: values.full_name?.split(' ')?.slice(1).join(' '),
    confirmPassword: values.confirmPassword ?? values.password,
    referral_code: includeReferral ? String(values.referral_code || '').trim() : undefined,
    mobile: values.mobile?.number
      ? { calling_code: values.mobile.calling_code, number: values.mobile.number }
      : undefined,
  };
}

export function useAuthActions() {
  const login = useLoginMutation();
  const signup = useSignupMutation();
  const forgot = useForgotPasswordMutation();
  const verify = useVerifyOtpMutation();
  const resend = useResendOtpMutation();
  const reset = useResetPasswordMutation();
  const mergeGuestCart = useCartMerge();
  const toast = useToast();
  const t = useCopy();

  // Runs one API step; on failure shows field errors on `form`, else a toast.
  const attempt = async (form, fallbackMessage, step, aliases) => {
    try {
      return await step();
    } catch (err) {
      if (form) applyApiFieldErrors(err, form, aliases);
      if (!err?.fields) toast.apiError(err, fallbackMessage);
      return null;
    }
  };

  const validate = (schema, input, form, aliases) => {
    const parsed = schema.safeParse(input);
    if (!parsed.success) {
      if (form) applyZodErrors(parsed.error, form, aliases);
      return null;
    }
    return parsed.data;
  };

  const startOtp = async ({
    form,
    purpose,
    contact,
    request,
    successMessage,
    failMessage,
    aliases,
    interceptResponse,
  }) =>
    attempt(
      form,
      failMessage,
      async () => {
        const resp = await request();
        const intercepted = interceptResponse?.(resp);
        if (intercepted) return intercepted;
        const token = extractOtpToken(resp) || '';
        saveOtpToken({ email: contact.email, purpose, token });
        toast.success(successMessage);
        return { contact, purpose, token, expiresAt: Date.now() + OTP_EXPIRY_MS };
      },
      aliases,
    );

  return {
    pending: {
      login: login.isPending,
      signup: signup.isPending,
      forgot: forgot.isPending,
      verify: verify.isPending,
      resend: resend.isPending,
      reset: reset.isPending,
    },
    errors: {
      login: login.error,
      signup: signup.error,
      forgot: forgot.error,
      verify: verify.error,
      resend: resend.error,
      reset: reset.error,
    },
    /** Clears mutation state (e.g. when the modal closes). */
    resetState() {
      [login, signup, forgot, verify, resend, reset].forEach((m) => m.reset());
    },

    /** @returns {Promise<boolean>} signed in */
    async login(input, { form } = {}) {
      const data = validate(loginSchema, input, form);
      if (!data) return false;
      const done = await attempt(form, t('signInFailed', 'Sign-in failed.'), async () => {
        await login.mutateAsync(data);
        return true;
      });
      if (!done) return false;
      const { merged = 0, failed = 0 } = (await mergeGuestCart()) ?? {};
      if (failed > 0) {
        toast.warning(
          t(
            'failedToSyncCart',
            'Signed in. {failed} cart item(s) failed to sync — open your cart to retry.',
          ).replace('{failed}', failed),
        );
      } else if (merged > 0) {
        toast.success(
          t('signedInSynced', 'Signed in. Synced {merged} cart item(s).').replace(
            '{merged}',
            merged,
          ),
        );
      } else {
        toast.success(t('signedIn', 'Signed in.'));
      }
      return true;
    },

    /** @returns {Promise<{contact, purpose, token, expiresAt}|null>} */
    async startSignup(input, { form, successMessage } = {}) {
      const aliases =
        input?.type === CUSTOMER_TYPE.BUSINESS ? BUSINESS_CONTACT_ERROR_ALIASES : undefined;
      const data = validate(signupSchema, input, form, aliases);
      if (!data) return null;
      const { confirmPassword: _confirm, ...payload } = data;
      if (!payload.referral_code) delete payload.referral_code;
      return startOtp({
        form,
        purpose: OTP_PURPOSE.SIGNUP,
        contact: { email: payload.email, mobile: payload.mobile },
        request: () => signup.mutateAsync(payload),
        successMessage: successMessage ?? t('checkEmailForOtp', 'Check your email for the OTP.'),
        failMessage: t('signUpFailed', 'Sign-up failed.'),
        aliases,
        // Business signups wait for merchant approval instead of sending an OTP.
        interceptResponse: (resp) =>
          isAwaitingApproval(resp)
            ? {
                awaitingApproval: true,
                message: t(
                  'signupAwaitingApproval',
                  "Your profile is not approved yet. Once it's approved, you'll receive an email from the merchant.",
                ),
              }
            : null,
      });
    },

    /** @returns {Promise<{contact, purpose, token, expiresAt}|null>} */
    async startReset(input, { form, schema } = {}) {
      const contact = schema ? validate(schema, input, form) : input;
      if (!contact) return null;
      return startOtp({
        form,
        purpose: OTP_PURPOSE.RESET_PASSWORD,
        contact,
        request: () => forgot.mutateAsync(contact),
        successMessage: t('otpSent', 'OTP sent.'),
        failMessage: t('couldNotStartPasswordReset', 'Could not start password reset.'),
      });
    },

    /** @returns {Promise<boolean>} verified */
    async verifyOtp({ contact, otp, purpose, token, ...extra }, { form, schema } = {}) {
      const resolvedToken = token || readOtpToken({ email: contact?.email, purpose }) || undefined;
      // `extra` carries screen fields such as the new password on a reset OTP.
      let input = { ...extra, ...contact, otp, purpose, token: resolvedToken };
      if (schema) {
        input = validate(schema, input, form);
        if (!input) return false;
      }
      const done = await attempt(
        form,
        t('couldNotVerifyOtp', 'Could not verify OTP.'),
        async () => {
          await verify.mutateAsync(input);
          return true;
        },
      );
      if (done) clearOtpToken({ email: contact?.email, purpose });
      return Boolean(done);
    },

    /** @returns {Promise<boolean>} password changed */
    async resetPassword(contact, values, { form } = {}) {
      const done = await attempt(
        form,
        t('couldNotResetPassword', 'Could not reset password.'),
        async () => {
          await reset.mutateAsync({ ...contact, ...values });
          return true;
        },
      );
      if (done) toast.success(t('passwordResetSuccess', 'Password reset. Sign in now.'));
      return Boolean(done);
    },

    /** @returns {Promise<{token, expiresAt}|null>} */
    async resendOtp(contact, purpose, { form } = {}) {
      return attempt(form, t('couldNotResendOtp', 'Could not resend OTP.'), async () => {
        const resp = await resend.mutateAsync(contact);
        const token = extractOtpToken(resp) || '';
        if (token) saveOtpToken({ email: contact.email, purpose, token });
        toast.success(t('otpResent', 'OTP resent.'));
        return { token, expiresAt: Date.now() + OTP_EXPIRY_MS };
      });
    },
  };
}
